import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WideLayout } from '../../../shared/layout/widelayout';
import { createPatientV2, type PatientV2CreatePayload } from '../../../services/patients/patientsV2Service';
import { PatientDetailsLayout } from '../usecases/v2/PatientDetailsLayout';
import type { PatientDocumentUploadFile } from '../usecases/v2/PatientDetailsForm';
import type { PatientV2Detail } from '../usecases/v2/types';

const emptyPatient: PatientV2Detail = {
  id: 'new',
  patient: '',
  cns: '',
  urs: '',
  crs: '',
  requestedAt: '',
  sla: '',
  slaTone: 'info',
  regulatoryStatus: 'Em preenchimento',
  sap: '',
  cpf: '',
  birthDate: '',
  email: '',
  cellphone: '',
  phoneNumber: '',
  phones: '',
  cep: '',
  city: '',
  uf: '',
  address: '',
  number: '',
  neighborhood: '',
  addressLine2: '',
  companyId: 0,
  sts: '',
  doctor: '',
  clinical: {
    cidPrimary: '',
    cidSecondary: '',
    diagnosis: '',
    oxygenIndication: '',
    oxygenSource: '',
    oxygenFlow: '',
    dailyUse: '',
    requestedEquipment: '',
    requestingDoctorName: '',
    requestingDoctorCrm: '',
    requestDate: '',
  },
  documents: [],
  history: [],
  protocol: '',
};

const toCreatePayload = (patient: PatientV2Detail): PatientV2CreatePayload => ({
  patient: patient.patient,
  sap: patient.sap,
  cpf: patient.cpf,
  birthDate: patient.birthDate,
  email: patient.email,
  cellphone: patient.cellphone,
  phoneNumber: patient.phoneNumber,
  cep: patient.cep,
  address: patient.address,
  number: patient.number,
  addressLine2: patient.addressLine2,
  neighborhood: patient.neighborhood,
  city: patient.city,
  uf: patient.uf,
  companyId: patient.companyId,
  clinicalInformation: {
    cidPrimary: patient.clinical.cidPrimary,
    cidSecondary: patient.clinical.cidSecondary,
    clinicalDiagnosis: patient.clinical.diagnosis,
    oxygenIndication: patient.clinical.oxygenIndication,
    prescribedOxygenSource: patient.clinical.oxygenSource,
    oxygenFlowLMin: patient.clinical.oxygenFlow,
    dailyUseHours: patient.clinical.dailyUse,
    requestedEquipment: patient.clinical.requestedEquipment,
    requestingDoctorName: patient.clinical.requestingDoctorName,
    requestingDoctorCrm: patient.clinical.requestingDoctorCrm,
    requestDate: patient.clinical.requestDate,
  },
  documents: [],
});

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Não foi possível ler o arquivo ${file.name}.`));
    reader.onload = () => resolve(String(reader.result ?? '').replace(/^data:[^;]+;base64,/, ''));
    reader.readAsDataURL(file);
  });

export const PatientV2CreatePage = () => {
  const navigate = useNavigate();

  const create = async (patient: PatientV2Detail, documents: PatientDocumentUploadFile[]) => {
    const payload = toCreatePayload(patient);
    payload.documents = await Promise.all(
      documents.map(async (file) => ({
        documentType: file.documentType,
        customDocumentType: file.customDocumentType,
        documentGeneratedAt: file.documentGeneratedAt,
        originalFileName: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
        contentBase64: await readFileAsBase64(file),
      }))
    );
    const result = await createPatientV2(payload);
    navigate(`/patientsv2/${result.id}`, { replace: true });
  };

  return <WideLayout outlet={<PatientDetailsLayout mode="create" patient={emptyPatient} onBack={() => navigate('/patientsv2')} onCreate={create} />} />;
};
